import { EntityLabelsTranslationService } from '@gfe/core/services/entity-labels-translation/entity-labels-translation.service';
import { dedup, isNil } from '@gfe/core/utils/shared-utils';
import {
  formatDuration,
  formatDurationFromIso,
  fromSecondsToDuration
} from '@gfe/core/utils/time-utils';
import { CalendarEvent } from '@gfe/features/components/visualization/calendar/models/calendar.models';
import { VisuItemLabel } from '@gfe/features/components/visualization/common/models/label.model';
import {
  GaliaMarker,
  MarkerType
} from '@gfe/features/components/visualization/mapbox/models/galia-marker';
import { GaliaRoute } from '@gfe/features/components/visualization/mapbox/models/galia-route';
import {
  Task,
  TaskType,
  TimelineRow
} from '@gfe/features/components/visualization/timeline/models/timeline';

import { VisualizationModule } from '@core/models/data-visualization/data-visualization.model';
import {
  AuxiliaryDataBundle,
  EntityInterface,
  TableColCfg
} from '@core/models/entities/entity-visualization-interface';
import { VisualizationBuilders } from '@core/models/entities/generic-entity';
import { EntityCfgOverrides } from '@core/models/entities/generic-entity-cfg';
import {
  QuantifiedExpertise,
  Quantity
} from '@core/models/entities/implementations/quantified-expertise';
import { DateTimeWindow } from '@core/models/entities/implementations/time-window';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';

import { FilterDataType } from '@shared/components/filter-query-builder/filter.models';
import { TableCellDataType } from '@shared/components/table/table-cell/table-cell.component';
import { formatTime } from '@shared/components/table/table-cell/utils';

import { deliveryColor, pickupColor, stringToColor } from '../../colors.model';
import {
  KpiIds,
  KpiModel,
  RawKpi,
  arrayOfStrKpis,
  dbKpiId,
  keyValueMapKpis,
  kpiType,
  summariesForType
} from '../../kpi.model';
import { Coords } from './coords';
import { CustomData } from './custom-data';
import { UserRecurrenceCfg } from './user-job-recurring-visit-v1';
import {
  RouteActionType,
  UserRouteActionV1Model,
  computeJobServiceWindowInAction,
  computeTemperatureSegments,
  createActionLabels,
  getJobActionType,
  heldTemperatureUids
} from './user-route-action-v1';
import { DEFAULT_ROUTE_COLOR, UserRouteV1Model, getRouteSiteColor } from './user-route-v1';
import { UserSiteV1Model } from './user-site-v1';

export type SiteSpecification =
  | 'AnyDepot' // Any Site labeled as Depot
  | 'VehicleStart' // The start Site of the Vehicle performing the job
  | { specific_site: string }; // A specific site, identified by its uid

export interface Operation {
  datetime_windows?: DateTimeWindow[];
  site: SiteSpecification;
  duration: string; // in ISO8601
  fixed_cost?: number;
}

export interface JobEchelonAlternative {
  site: SiteSpecification;
  coords?: Coords;
  pickup_to_echelon_cost?: number;
  echelon_to_delivery_cost?: number;
  pickup_duration: string; // in ISO8601
  delivery_duration: string; // in ISO8601
  min_lag: string; // in ISO8601
  max_lag?: string; // in ISO8601
}

export interface JobEchelon {
  is_optional: boolean;
  alternatives: JobEchelonAlternative[];
}

export interface MultiEchelonCfg {
  echelons: JobEchelon[];
}

export type UserVehicleRegistrationAssignment =
  | { code: string } // Named copy of a Nominal (Registered) vehicle
  | { copy: number }; // Numbered copy (1-based) of a Generic vehicle

export interface VehiclePreAssignment {
  vehicle_uid: string;
  registration?: UserVehicleRegistrationAssignment;
}

export interface SplitCfg {
  split_ratio: number;
  max_nb_splits?: number | null;
}

export interface UserJobTransportOrderV1Model {
  uid: string;
  name: string;
  pickup: Operation[];
  multi_echelon_cfg?: MultiEchelonCfg;
  delivery: Operation[];
  transported_commodity_uid?: string;
  transported_product_uid?: string;
  kind_uid?: string;
  fulfillment_reward?: number;
  externalization_cost?: number;
  is_reverse_logistics: boolean;
  expertise_requirements: QuantifiedExpertise[];
  vehicle_capacity_requirement: Quantity[];
  split_cfg?: SplitCfg | null;
  custom_data: CustomData;
  kpis: RawKpi[];
  formattedKpis?: Record<string, unknown>;
  pre_assigned_route_uids?: string[];
  vehicle_pre_assignments?: VehiclePreAssignment[];
  pre_assigned_company_uids?: string[];
  equipment_requirement_uids?: string[];
  recurrence_cfg?: UserRecurrenceCfg;
  valid_horizon?: DateTimeWindow;
  // Descriptive only, ignored by the solver: where the goods were really prepared when
  // that differs from the pickup site, and which site really ordered them when that
  // differs from the delivery site.
  preparing_site_uid?: string | null;
  ordering_site_uid?: string | null;
}

export type UserJobTransportOrderV1InputModel = Omit<UserJobTransportOrderV1Model, 'uid'>;

export function getOperationSite(siteSpec: SiteSpecification): string {
  if (siteSpec === 'AnyDepot') return 'AnyDepot';
  if (siteSpec === 'VehicleStart') return 'VehicleStart';
  return (siteSpec as { specific_site: string }).specific_site;
}

export function getSpecificSiteUid(
  siteSpec: SiteSpecification | null | undefined
): string | undefined {
  if (siteSpec != null && typeof siteSpec === 'object' && 'specific_site' in siteSpec) {
    return siteSpec.specific_site;
  }
  return undefined;
}

/**
 * Overrides for an entity carrying a `site: SiteSpecification` field. The generic
 * generator renders that union as a raw object cell (`{"specific_site":"..."}`);
 * collapse it to the site uid and tag it as a SITE reference so it renders as a uid
 * chip. Sub-tables disable column filtering, so the `filterCfg` only serves to surface
 * the SITE reference type (clickable / name-resolvable chip).
 */
export function siteSpecificationCfgOverrides<
  T extends { site: SiteSpecification }
>(): EntityCfgOverrides<T> {
  return {
    buildColumns: (
      autoColumns: TableColCfg<EntityInterface<T>>[]
    ): TableColCfg<EntityInterface<T>>[] =>
      autoColumns.map((col) => {
        if ((col.key as string) !== 'raw.site') return col;
        return {
          ...col,
          type: 'uid',
          renderHook: (value: unknown): unknown =>
            isNil(value) ? undefined : getOperationSite(value as SiteSpecification),
          filterCfg: {
            translationLabel: 'site',
            dbColName: 'site',
            filterType: FilterDataType.UID,
            uidEntityType: 'SITE'
          }
        };
      })
  };
}

/** A transport-order operation edited as a standalone `UserJobOperation` entity. */
export interface UserJobOperationModel extends Operation {
  uid?: string;
  job_uid: string;
  kind: 'Pickup' | 'Delivery' | 'Service';
  name?: string;
}

export const jobOperationCfgOverrides =
  siteSpecificationCfgOverrides<UserJobOperationModel>();

/**
 * Renders a single vehicle pre-assignment as readable text in the form
 * `vehicle_uid: registration`. The registration value is the named `code`
 * for a registered (nominal) vehicle, or the `copy` index for a generic
 * vehicle. When no registration is set, only the vehicle uid is shown.
 */
function formatVehiclePreAssignment(assignment: VehiclePreAssignment): string {
  const registration = assignment.registration;
  if (isNil(registration)) return assignment.vehicle_uid;
  const value = 'code' in registration ? registration.code : registration.copy;
  return `${assignment.vehicle_uid}: ${value}`;
}

/**
 * Joins every vehicle pre-assignment of a job into a single comma-separated,
 * human-readable string. Returns an empty string when there are none.
 */
export function formatVehiclePreAssignments(
  assignments: VehiclePreAssignment[] | undefined
): string {
  if (isNil(assignments) || assignments.length === 0) return '';
  return assignments.map(formatVehiclePreAssignment).join(', ');
}

/** Compared as instants: two starts written with different offsets do not sort as text. */
function earliestStart(windows: DateTimeWindow[]): string | undefined {
  return windows.reduce<DateTimeWindow | undefined>(
    (earliest, window) =>
      !earliest || Date.parse(window.start) < Date.parse(earliest.start)
        ? window
        : earliest,
    undefined
  )?.start;
}

function createOperationLabels(jobUid: string, operation: Operation): VisuItemLabel[] {
  return [
    { label: 'site', level: 0, value: getOperationSite(operation.site) },
    { label: 'job', level: 0, value: jobUid }
  ];
}

/** Popover content for a multi-echelon alternative pin. */
function createEchelonAlternativeLabels(
  jobUid: string,
  echelonNumber: number,
  isOptional: boolean,
  alternative: JobEchelonAlternative
): VisuItemLabel[] {
  const labels: VisuItemLabel[] = [
    { label: 'site', level: 0, value: getOperationSite(alternative.site) },
    { label: 'job', level: 0, value: jobUid },
    { label: 'echelons', level: 0, value: String(echelonNumber) }
  ];

  if (isOptional) {
    labels.push({ label: 'echelon_is_optional', level: 1, value: 'true' });
  }
  labels.push({
    label: 'echelon_pickup_duration',
    level: 1,
    value: formatDurationFromIso(alternative.pickup_duration)
  });
  labels.push({
    label: 'echelon_delivery_duration',
    level: 1,
    value: formatDurationFromIso(alternative.delivery_duration)
  });
  if (!isNil(alternative.pickup_to_echelon_cost)) {
    labels.push({
      label: 'echelon_pickup_to_echelon_cost',
      level: 1,
      value: String(alternative.pickup_to_echelon_cost)
    });
  }
  if (!isNil(alternative.echelon_to_delivery_cost)) {
    labels.push({
      label: 'echelon_echelon_to_delivery_cost',
      level: 1,
      value: String(alternative.echelon_to_delivery_cost)
    });
  }

  return labels;
}

/**
 * Floating map pins for every multi-echelon alternative site of a job. These are
 * candidate intermediate stops (not part of a route), so they render as neutral
 * `basic` markers positioned by the alternative's own coords, falling back to the
 * referenced site's coords from auxiliary data.
 */
function buildEchelonMarkers(
  raw: UserJobTransportOrderV1Model,
  auxiliarySites: Record<string, UserSiteV1Model>,
  color: string
): GaliaMarker[] {
  const markers: GaliaMarker[] = [];
  const echelonSiteLabel = EntityLabelsTranslationService.translateLabelStatic(
    'table.columns.echelon_site'
  );

  raw.multi_echelon_cfg?.echelons.forEach((echelon, echelonIdx) => {
    echelon.alternatives.forEach((alternative, altIdx) => {
      const siteUid = getSpecificSiteUid(alternative.site);
      const coords =
        alternative.coords ?? (siteUid ? auxiliarySites[siteUid]?.coords : undefined);
      if (isNil(coords)) return;

      markers.push({
        uid: `${raw.uid}_echelon${echelonIdx}_alt${altIdx}`,
        type: 'basic' as MarkerType,
        title: getOperationSite(alternative.site),
        coords,
        color,
        description: {
          name: raw.uid,
          type: echelonSiteLabel,
          content: createEchelonAlternativeLabels(
            raw.uid,
            echelonIdx + 1,
            echelon.is_optional,
            alternative
          )
        },
        structuralAggregatorIds: [raw.uid]
      });
    });
  });

  return markers;
}

/** A single scheduled service of the job within one route action. */
interface JobTimelineStop {
  routeUid: string;
  vehicleUid?: string;
  action: UserRouteActionV1Model;
  type: RouteActionType;
  start: Date;
  finish: Date;
}

/** Route-action translation key for the work block naming a job stop. */
function jobStopTaskName(type: RouteActionType): string {
  const key =
    type === 'delivery'
      ? 'delivery'
      : type === 'delivery-pickup'
        ? 'pickup_delivery'
        : type === 'pickup'
          ? 'pickup'
          : 'service';
  return EntityLabelsTranslationService.translateRouteActionStatic(key);
}

/**
 * Collects every action, across the job's assigned routes, that services this job
 * (via load/unloads or, for no-load service stops, via handled_jobs), together with
 * the job-scoped service window and the pickup/delivery nature of the stop.
 */
function collectJobStops(
  job: UserJobTransportOrderV1Model,
  routes: UserRouteV1Model[]
): JobTimelineStop[] {
  const stops: JobTimelineStop[] = [];
  for (const route of routes) {
    for (const action of route.actions) {
      const window = computeJobServiceWindowInAction(action, job.uid);
      if (isNil(window)) continue;
      stops.push({
        routeUid: route.uid,
        vehicleUid: route.vehicle_uid,
        action,
        type: getJobActionType(action, job.uid),
        start: window.start,
        finish: window.finish
      });
    }
  }
  // Chronological order so a job's pickups precede the deliveries they feed.
  stops.sort((a, b) => a.start.getTime() - b.start.getTime());
  return stops;
}

/**
 * Geographic position of a job stop, preferring the action's own coords and
 * falling back to the referenced site's coords from auxiliary data.
 */
function resolveStopCoords(
  stop: JobTimelineStop,
  auxiliarySites: Record<string, UserSiteV1Model>
): Coords | undefined {
  return stop.action.coords ?? auxiliarySites[stop.action.site_uid]?.coords;
}

const actsAsPickup = (type: RouteActionType): boolean =>
  type === 'pickup' || type === 'delivery-pickup';
const actsAsDelivery = (type: RouteActionType): boolean =>
  type === 'delivery' || type === 'delivery-pickup';

/**
 * Chooses the connector between two consecutive stops:
 * - `solid` while the goods travel on a vehicle (pickup -> delivery);
 * - `dashed` while they wait at an intermediate site between legs
 *   (delivery -> pickup, the multi-echelon case);
 * - `undefined` for any other transition, which is left unconnected.
 *
 * A `delivery-pickup` stop (goods both dropped off and picked up in the same
 * action) satisfies either role, so it can open or close either connector.
 */
function stopConnectorStyle(
  previous: RouteActionType,
  current: RouteActionType
): 'solid' | 'dashed' | undefined {
  if (actsAsPickup(previous) && actsAsDelivery(current)) return 'solid';
  if (actsAsDelivery(previous) && actsAsPickup(current)) return 'dashed';
  return undefined;
}

/**
 * Colors a job stop's own site if it has one assigned, otherwise the site of the most
 * recent pickup that fed it - the "loading site" a delivery block is color-coded by.
 * Mirrors the route builder's `computeColorSegments`, simplified to a single job with
 * no cross-job quantity split.
 */
function computeJobColorSegments(
  siteUid: string,
  loadingSiteUid: string | undefined,
  auxiliarySites: Record<string, UserSiteV1Model>
): Array<{ color: string; percentage: number }> | undefined {
  const siteColor = getRouteSiteColor(siteUid, auxiliarySites);
  if (siteColor !== DEFAULT_ROUTE_COLOR) return [{ color: siteColor, percentage: 100 }];
  if (isNil(loadingSiteUid)) return undefined;
  return [{ color: getRouteSiteColor(loadingSiteUid, auxiliarySites), percentage: 100 }];
}

/** Labels for the wait block between a delivery and the pickup that lifts the goods again. */
function createJobWaitTaskLabels(
  siteUid: string,
  previousFinish: Date,
  start: Date
): VisuItemLabel[] {
  const waitSeconds = Math.max(0, (start.getTime() - previousFinish.getTime()) / 1000);
  return [
    { label: 'site', level: 0, value: siteUid },
    {
      label: 'duration',
      level: 0,
      value: formatDuration(fromSecondsToDuration(waitSeconds))
    }
  ];
}

/**
 * Builds the ordered timeline tasks for a job: one `work` block per stop, joined
 * by a `drive` connector between a pickup and the delivery it feeds (solid, goods
 * on a vehicle) or by a `wait` block between a delivery and the pickup that lifts
 * the goods again (multi-echelon wait at the intermediate site). Consecutive
 * stops of any other kinds are left unconnected.
 */
function buildJobStopTasks(
  stops: JobTimelineStop[],
  auxiliarySites: Record<string, UserSiteV1Model>
): Task[] {
  const tasks: Task[] = [];
  let taskId = 0;
  let loadingSiteUid: string | undefined;

  stops.forEach((stop, index) => {
    const connector =
      index > 0 ? stopConnectorStyle(stops[index - 1].type, stop.type) : undefined;
    if (!isNil(connector)) {
      const previous = stops[index - 1];
      if (connector === 'dashed') {
        tasks.push({
          id: taskId++,
          siteUid: stop.action.site_uid,
          kind: 'wait',
          type: 'basic',
          name: EntityLabelsTranslationService.translateRouteActionStatic('wait'),
          startDate: previous.finish,
          endDate: stop.start,
          labels: createJobWaitTaskLabels(
            stop.action.site_uid,
            previous.finish,
            stop.start
          ),
          highlightAggregatorIds: [],
          temperatureUids: heldTemperatureUids(previous.action.holdings),
          temperatureSegments: computeTemperatureSegments(previous.action.holdings)
        });
      } else {
        tasks.push({
          id: taskId++,
          siteUid: stop.action.site_uid,
          kind: 'drive',
          type: 'basic',
          name: '',
          dashedLine: false,
          startDate: previous.finish,
          endDate: stop.start,
          labels: [],
          highlightAggregatorIds: [],
          temperatureUids: heldTemperatureUids(previous.action.holdings),
          temperatureSegments: computeTemperatureSegments(previous.action.holdings)
        });
      }
    }

    tasks.push({
      id: taskId++,
      siteUid: stop.action.site_uid,
      kind: 'work',
      type: stop.type as TaskType,
      name: jobStopTaskName(stop.type),
      startDate: stop.start,
      endDate: stop.finish,
      labels: createActionLabels(stop.action, stop.routeUid, stop.vehicleUid),
      highlightAggregatorIds: [],
      routeUid: stop.routeUid,
      actionUid: stop.action.uid,
      temperatureUids: heldTemperatureUids(stop.action.holdings),
      temperatureSegments: computeTemperatureSegments(stop.action.holdings),
      colorSegments: computeJobColorSegments(
        stop.action.site_uid,
        loadingSiteUid,
        auxiliarySites
      )
    });

    if (actsAsPickup(stop.type)) {
      loadingSiteUid = stop.action.site_uid;
    }
  });

  return tasks;
}

function resolveOperationCoords(
  operation: Operation,
  auxiliarySites: Record<string, UserSiteV1Model>
): Coords | undefined {
  const siteUid = getSpecificSiteUid(operation.site);
  if (siteUid === undefined) return undefined;
  return auxiliarySites[siteUid]?.coords;
}

const coordKey = (c: Coords): string => `${c.lat},${c.lng}`;

export function buildChainedPolylines(
  pickupCoords: Coords[],
  deliveryCoords: Coords[]
): Coords[][] {
  const seen = new Set<string>();
  const polylines: Coords[][] = [];

  for (const p of pickupCoords) {
    for (const d of deliveryCoords) {
      const key = `${coordKey(p)}|${coordKey(d)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      polylines.push([p, d]);
    }
  }

  return polylines;
}

function computeFormattedKpis(kpis: RawKpi[]): Record<string, unknown> {
  const formattedKpis: Record<string, unknown> = {};

  kpis.forEach((rawKpi) => {
    const kpiModel = new KpiModel(rawKpi);
    if (kpiModel.isMapType()) {
      formattedKpis[rawKpi.id] = kpiModel.getformattedMap();
    } else if (kpiModel.isArrayType()) {
      formattedKpis[rawKpi.id] = kpiModel.getformattedArray();
    } else {
      formattedKpis[rawKpi.id] = kpiModel.value;
    }
  });

  return formattedKpis;
}

const NUMERIC_SORTABLE_COLUMNS = ['externalization_cost', 'fulfillment_reward'] as const;

export const jobTransportOrderCfgOverrides: EntityCfgOverrides<UserJobTransportOrderV1Model> =
  {
    // Timeline is derived from the job's assigned route actions (see
    // buildTimelineRows); it is not declared on the upload schema, so the
    // supported set is overridden explicitly here.
    customVisualizations: new Set<VisualizationModule>(['map', 'calendar', 'timeline']),
    preprocessEntity: (
      raw: UserJobTransportOrderV1Model
    ): UserJobTransportOrderV1Model => ({
      ...raw,
      formattedKpis: computeFormattedKpis(raw.kpis)
    }),
    buildColumns: (
      autoColumns: TableColCfg<EntityInterface<UserJobTransportOrderV1Model>>[]
    ): TableColCfg<EntityInterface<UserJobTransportOrderV1Model>>[] => {
      const cols = autoColumns.map((col) => {
        if ((col.key as string) === 'raw.multi_echelon_cfg') {
          return {
            ...col,
            renderHook: (value: unknown) => {
              if (isNil(value)) return '';
              return JSON.stringify(value);
            }
          };
        }
        if ((col.key as string) === 'raw.split_cfg') {
          return {
            ...col,
            type: 'string-number-map' as const,
            renderHook: (value: unknown) => {
              if (isNil(value)) return {};
              const cfg = value as SplitCfg;
              const splitRatioLabel = EntityLabelsTranslationService.translateLabelStatic(
                'table.columns.split_ratio'
              );
              const maxNbSplitsLabel = EntityLabelsTranslationService.translateLabelStatic(
                'table.columns.max_nb_splits'
              );
              const result: Record<string, number> = {
                [splitRatioLabel]: cfg.split_ratio
              };
              if (!isNil(cfg.max_nb_splits)) {
                result[maxNbSplitsLabel] = cfg.max_nb_splits as number;
              }
              return result;
            }
          };
        }
        const numericColName = NUMERIC_SORTABLE_COLUMNS.find(
          (name) => (col.key as string) === `raw.${name}`
        );
        if (numericColName) {
          return {
            ...col,
            sortCfg: { dbColName: numericColName },
            filterCfg: {
              translationLabel: numericColName,
              dbColName: numericColName,
              filterType: FilterDataType.NUMBER
            }
          };
        }
        return col;
      });

      const nestedCols: TableColCfg<EntityInterface<UserJobTransportOrderV1Model>>[] = [
        {
          key: 'raw.pickup.datetime_windows.start' as keyof EntityInterface<UserJobTransportOrderV1Model>,
          label: 'table.columns.date',
          type: 'date',
          sortCfg: { dbColName: 'metadata.due_date' },
          filterCfg: {
            translationLabel: 'date',
            dbColName: 'metadata.due_date',
            filterType: FilterDataType.DATE
          },
          renderHook: (_value, row) => {
            if (isNil(row)) return undefined;
            return earliestStart(
              row.raw.delivery.flatMap((op) => op.datetime_windows ?? [])
            );
          }
        },
        {
          key: 'raw.pickup.duration' as keyof EntityInterface<UserJobTransportOrderV1Model>,
          label: 'table.columns.pickup_duration',
          type: 'durations',
          rawValue(row?: EntityInterface<UserJobTransportOrderV1Model>): unknown {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.pickup.map((op) => op.duration));
          },
          renderHook(_value, row) {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.pickup.map((op) => op.duration));
          }
        },
        {
          key: 'raw.pickup.site' as keyof EntityInterface<UserJobTransportOrderV1Model>,
          label: 'table.columns.pickup_site',
          type: 'uid-list',
          rawValue(row?: EntityInterface<UserJobTransportOrderV1Model>): unknown {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.pickup.map((op) => getOperationSite(op.site)));
          },
          renderHook(_value, row) {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.pickup.map((op) => getOperationSite(op.site)));
          },
          filterCfg: {
            translationLabel: 'pickup_site',
            dbColName: 'metadata.pickup_site_uids',
            filterType: FilterDataType.UID_LIST,
            uidEntityType: 'SITE'
          }
        },
        {
          key: 'raw.pickup.datetime_windows' as keyof EntityInterface<UserJobTransportOrderV1Model>,
          label: 'table.columns.pickup_window',
          type: 'datetime-windows',
          rawValue(row?: EntityInterface<UserJobTransportOrderV1Model>): unknown {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.pickup.flatMap((op) => op.datetime_windows ?? []));
          },
          renderHook(_value, row) {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.pickup.flatMap((op) => op.datetime_windows ?? []));
          }
        },
        {
          key: 'raw.pickup.fixed_cost' as keyof EntityInterface<UserJobTransportOrderV1Model>,
          label: 'table.columns.pickup_fixed_cost',
          type: 'number',
          rawValue(row?: EntityInterface<UserJobTransportOrderV1Model>): unknown {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.pickup.map((op) => op.fixed_cost));
          },
          renderHook(_value, row) {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.pickup.map((op) => op.fixed_cost));
          }
        },
        {
          key: 'raw.delivery.site' as keyof EntityInterface<UserJobTransportOrderV1Model>,
          label: 'table.columns.delivery_site',
          type: 'uid-list',
          rawValue(row?: EntityInterface<UserJobTransportOrderV1Model>): unknown {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.delivery.map((op) => getOperationSite(op.site)));
          },
          renderHook(_value, row) {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.delivery.map((op) => getOperationSite(op.site)));
          },
          filterCfg: {
            translationLabel: 'delivery_site',
            dbColName: 'metadata.delivery_site_uids',
            filterType: FilterDataType.UID_LIST,
            uidEntityType: 'SITE'
          }
        },
        {
          key: 'raw.delivery.duration' as keyof EntityInterface<UserJobTransportOrderV1Model>,
          label: 'table.columns.delivery_duration',
          type: 'durations',
          rawValue(row?: EntityInterface<UserJobTransportOrderV1Model>): unknown {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.delivery.map((op) => op.duration));
          },
          renderHook(_value, row) {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.delivery.map((op) => op.duration));
          }
        },
        {
          key: 'raw.delivery.datetime_windows' as keyof EntityInterface<UserJobTransportOrderV1Model>,
          label: 'table.columns.delivery_window',
          type: 'datetime-windows',
          rawValue(row?: EntityInterface<UserJobTransportOrderV1Model>): unknown {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.delivery.flatMap((op) => op.datetime_windows ?? []));
          },
          renderHook(_value, row) {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.delivery.flatMap((op) => op.datetime_windows ?? []));
          }
        },
        {
          key: 'raw.delivery.fixed_cost' as keyof EntityInterface<UserJobTransportOrderV1Model>,
          label: 'table.columns.delivery_fixed_cost',
          type: 'number',
          rawValue(row?: EntityInterface<UserJobTransportOrderV1Model>): unknown {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.delivery.map((op) => op.fixed_cost));
          },
          renderHook(_value, row) {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return dedup(job.delivery.map((op) => op.fixed_cost));
          }
        },
        {
          key: 'raw.vehicle_pre_assignments' as keyof EntityInterface<UserJobTransportOrderV1Model>,
          label: 'table.columns.pre_assigned_vehicles',
          type: 'string',
          rawValue(row?: EntityInterface<UserJobTransportOrderV1Model>): unknown {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return formatVehiclePreAssignments(job.vehicle_pre_assignments);
          },
          renderHook(_value, row) {
            const job = row?.raw;
            if (isNil(job)) return undefined;
            return formatVehiclePreAssignments(job.vehicle_pre_assignments);
          }
        }
      ];

      const kpiDefs: {
        id: KpiIds;
        filterable?: boolean;
        entityType?: string;
        type?: TableCellDataType;
      }[] = [
        { id: 'assigned_routes', type: 'uid-list', filterable: true, entityType: 'ROUTE' },
        {
          id: 'assigned_vehicles',
          type: 'uid-list',
          filterable: true,
          entityType: 'VEHICLE'
        },
        {
          id: 'assigned_companies',
          type: 'uid-list',
          filterable: true,
          entityType: 'COMPANY'
        }
      ];

      const kpiCols: TableColCfg<EntityInterface<UserJobTransportOrderV1Model>>[] =
        kpiDefs.map((kpi) => {
          const type: TableCellDataType =
            kpi.type ??
            (keyValueMapKpis.has(kpi.id)
              ? 'string-number-map'
              : arrayOfStrKpis.has(kpi.id)
                ? 'uid-list'
                : 'string');
          const summaryTypes = summariesForType[kpiType[kpi.id]];

          const col: TableColCfg<EntityInterface<UserJobTransportOrderV1Model>> = {
            key: ('raw.kpis.' +
              kpi.id) as keyof EntityInterface<UserJobTransportOrderV1Model>,
            label: 'kpis.' + kpi.id,
            type,
            rawValue(row?: EntityInterface<UserJobTransportOrderV1Model>): unknown {
              const job = row?.raw;
              if (isNil(job)) return undefined;
              return job.kpis.find((k) => k.id === kpi.id)?.value;
            },
            renderHook(
              _value: unknown,
              row?: EntityInterface<UserJobTransportOrderV1Model>
            ) {
              const job = row?.raw;
              const emptyValue = type === 'string' ? '' : {};
              if (isNil(job) || isNil(job.formattedKpis)) return emptyValue;
              return job.formattedKpis[kpi.id] ?? emptyValue;
            },
            sortCfg: { dbColName: '#assoc:kpi.' + (dbKpiId[kpi.id] ?? kpi.id) },
            summaryTypes
          };

          if (kpi.filterable) {
            col.filterCfg = {
              translationLabel: dbKpiId[kpi.id] ?? kpi.id,
              dbColName: `#assoc:kpi.${dbKpiId[kpi.id] ?? kpi.id}`,
              filterType: FilterDataType.UID_LIST,
              uidEntityType: kpi.entityType
            };
          }

          return col;
        });

      const insertIdx = cols.findIndex((c) => (c.key as string) === 'raw.kind_uid');
      if (insertIdx >= 0) {
        cols.splice(insertIdx + 1, 0, ...nestedCols);
        return [...cols, ...kpiCols];
      }
      return [...cols, ...nestedCols, ...kpiCols];
    }
  };

export const jobTransportOrderVisualizationBuilders: VisualizationBuilders<UserJobTransportOrderV1Model> =
  {
    buildMarkers: (
      raw: UserJobTransportOrderV1Model,
      auxData?: AuxiliaryDataBundle
    ): GaliaMarker[] => {
      const auxiliarySites: Record<string, UserSiteV1Model> = Object.fromEntries(
        ((auxData?.entitiesArrayPerVersionName['UserSite'] ?? []) as UserSiteV1Model[]).map(
          (site) => [site.uid, site]
        )
      );
      const color = stringToColor(raw.uid);
      const markers: GaliaMarker[] = [];

      raw.pickup.forEach((op, i) => {
        const coords = resolveOperationCoords(op, auxiliarySites);
        if (!coords) return;
        const window = op.datetime_windows?.[0];
        markers.push({
          uid: `${raw.uid}pick${i}`,
          type: 'pickup' as MarkerType,
          title: getOperationSite(op.site),
          coords,
          color,
          description: {
            name: raw.uid,
            type: EntityLabelsTranslationService.translateRouteActionStatic('pickup'),
            startDate: window ? new Date(window.start) : undefined,
            endDate: window ? new Date(window.finish) : undefined,
            content: createOperationLabels(raw.uid, op)
          },
          structuralAggregatorIds: [raw.uid]
        });
      });

      raw.delivery.forEach((op, i) => {
        const coords = resolveOperationCoords(op, auxiliarySites);
        if (!coords) return;
        const window = op.datetime_windows?.[0];
        markers.push({
          uid: `${raw.uid}deliver${i}`,
          type: 'delivery' as MarkerType,
          title: getOperationSite(op.site),
          coords,
          color,
          description: {
            name: raw.uid,
            type: EntityLabelsTranslationService.translateRouteActionStatic('delivery'),
            startDate: window ? new Date(window.start) : undefined,
            endDate: window ? new Date(window.finish) : undefined,
            content: createOperationLabels(raw.uid, op)
          },
          structuralAggregatorIds: [raw.uid]
        });
      });

      markers.push(...buildEchelonMarkers(raw, auxiliarySites, color));

      return markers;
    },

    buildRoutes: (
      raw: UserJobTransportOrderV1Model,
      _markers,
      auxData?: AuxiliaryDataBundle
    ): GaliaRoute[] => {
      const auxiliarySites: Record<string, UserSiteV1Model> = Object.fromEntries(
        ((auxData?.entitiesArrayPerVersionName['UserSite'] ?? []) as UserSiteV1Model[]).map(
          (site) => [site.uid, site]
        )
      );
      const color = stringToColor(raw.uid);

      // When the job has been routed, trace its actual path: one polyline through
      // the sites of the route actions that reference it, in chronological order.
      const routes = (auxData?.entitiesArrayPerVersionName['UserRoute'] ??
        []) as UserRouteV1Model[];
      const stops = collectJobStops(raw, routes);
      if (stops.length > 0) {
        const coords = stops
          .map((stop) => resolveStopCoords(stop, auxiliarySites))
          .filter((c): c is Coords => !isNil(c));
        if (coords.length < 2) {
          return [];
        }
        return [
          {
            aggregatorUid: raw.uid,
            color,
            coords,
            name: raw.uid,
            uid: `${raw.uid}_actions`,
            width: 5
          }
        ];
      }

      const pickupCoords = raw.pickup
        .map((op) => resolveOperationCoords(op, auxiliarySites))
        .filter((c): c is Coords => !isNil(c));
      const deliveryCoords = raw.delivery
        .map((op) => resolveOperationCoords(op, auxiliarySites))
        .filter((c): c is Coords => !isNil(c));

      if (pickupCoords.length === 0 || deliveryCoords.length === 0) {
        return [];
      }

      const polylines = buildChainedPolylines(pickupCoords, deliveryCoords);
      return polylines.map((coords, i) => ({
        aggregatorUid: raw.uid,
        color,
        coords,
        name: raw.uid,
        uid: `${raw.uid}_option_${i}`,
        width: 5
      }));
    },

    buildCalendarEvents: (raw: UserJobTransportOrderV1Model): CalendarEvent[] => {
      const events: CalendarEvent[] = [];

      raw.pickup.forEach((op, opIndex) => {
        (op.datetime_windows ?? []).forEach((window, i) => {
          const cutoff =
            window.arrival_cutoff?.cutoff != null
              ? new Date(window.arrival_cutoff.cutoff)
              : null;

          events.push({
            uid: `${raw.uid}_pickup_${opIndex}_${i}`,
            title: `${raw.uid} - ${EntityLabelsTranslationService.translateRouteActionStatic('pickup')}`,
            start: new Date(window.start),
            end: new Date(window.finish),
            mainColor: pickupColor,
            cutoff,
            labels:
              cutoff != null
                ? [{ label: 'pickup_cutoff', level: 0, value: formatTime(cutoff) }]
                : []
          });
        });
      });

      raw.delivery.forEach((op, opIndex) => {
        (op.datetime_windows ?? []).forEach((window, i) => {
          const cutoff =
            window.arrival_cutoff?.cutoff != null
              ? new Date(window.arrival_cutoff.cutoff)
              : null;

          events.push({
            uid: `${raw.uid}_delivery_${opIndex}_${i}`,
            title: `${raw.uid} - ${EntityLabelsTranslationService.translateRouteActionStatic('delivery')}`,
            start: new Date(window.start),
            end: new Date(window.finish),
            mainColor: deliveryColor,
            cutoff,
            labels:
              cutoff != null
                ? [{ label: 'delivery_cutoff', level: 0, value: formatTime(cutoff) }]
                : []
          });
        });
      });

      return events;
    },

    buildTimelineRows: (
      raw: UserJobTransportOrderV1Model,
      auxData?: AuxiliaryDataBundle
    ): TimelineRow[] => {
      const routes = (auxData?.entitiesArrayPerVersionName['UserRoute'] ??
        []) as UserRouteV1Model[];
      const auxiliarySites: Record<string, UserSiteV1Model> = Object.fromEntries(
        ((auxData?.entitiesArrayPerVersionName['UserSite'] ?? []) as UserSiteV1Model[]).map(
          (site) => [site.uid, site]
        )
      );

      const stops = collectJobStops(raw, routes);
      if (stops.length === 0) return [];

      return [
        {
          uid: raw.uid,
          color: stringToColor(raw.uid),
          startDate: stops[0].start,
          endDate: stops[stops.length - 1].finish,
          labels: [],
          name: raw.name ?? raw.uid,
          tasks: buildJobStopTasks(stops, auxiliarySites)
        }
      ];
    }
  };

export function registerJobTransportOrderExtensions(
  registry: GenericEntityRegistryService
): void {
  registry.registerOverrides('UserJobTransportOrder', jobTransportOrderCfgOverrides);
  registry.registerOverrides('UserJobOperation', jobOperationCfgOverrides);
  registry.registerVisualizationBuilders(
    'UserJobTransportOrder',
    jobTransportOrderVisualizationBuilders
  );
}
